import React, { useState } from 'react';
import { Bell, Mail, Smartphone, X, Check, Loader } from 'lucide-react';
import { useNotificationPreferences } from '../Hooks/useNotificationPreferences';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows 
} from '../shared/config/theme';

const NotificationSettings = ({ userId, onClose }) => {
  const { preferences, loading, updatePreferences } = useNotificationPreferences(userId);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleToggle = async (key, value) => {
    setSaving(true);
    const result = await updatePreferences({ [key]: value });
    setSaving(false);

    if (result.success) {
      showNotification('Settings updated successfully!');
    } else {
      showNotification('Failed to update settings', 'error');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Loading notification settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Toast notification */}
      {notification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] animate-fade-in">
          <div className={`px-6 py-3 rounded-full shadow-lg flex items-center gap-2 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? (
              <Check className="w-4 h-4" />
            ) : (
              <X className="w-4 h-4" />
            )}
            {notification.message}
          </div>
        </div>
      )}

      {/* Master Toggles */}
      <div className="space-y-3 mb-6">
        <h4 className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">
          Channels
        </h4>
        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-orange-500" />
              <div>
                <h4 className="text-gray-800 font-semibold text-sm">Email Notifications</h4>
                <p className="text-gray-500 text-xs">Receive updates via email</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences?.email_notifications ?? true}
                onChange={(e) => handleToggle('email_notifications', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-orange-500" />
              <div>
                <h4 className="text-gray-800 font-semibold text-sm">Push Notifications</h4>
                <p className="text-gray-500 text-xs">Receive alerts on your device</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences?.push_notifications ?? true}
                onChange={(e) => handleToggle('push_notifications', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Notification Types */}
      <div className="space-y-3">
        <h4 className="text-gray-500 text-xs font-semibold uppercase tracking-wider">
          Notification Types
        </h4>

        {[
          { key: 'notify_session_reminder', label: 'Daily Session Reminders', desc: 'Get reminded to complete your daily session' },
          { key: 'notify_streak_milestone', label: 'Streak Milestones', desc: 'Celebrate your consistency achievements' },
          { key: 'notify_progress_updates', label: 'Weekly Progress Summary', desc: 'Review your weekly manifestation journey' },
          { key: 'notify_new_content', label: 'New Content & Features', desc: 'Be first to know about new journeys' },
          { key: 'notify_session_completion', label: 'Session Completion', desc: 'Get congratulated after completing sessions' },
          { key: 'notify_premium_features', label: 'Premium Features', desc: 'Learn about premium benefits and offers' },
        ].map((item) => (
          <div key={item.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
            <div className="flex-1">
              <h5 className="text-gray-800 font-medium text-sm">{item.label}</h5>
              <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0">
              <input
                type="checkbox"
                checked={preferences?.[item.key] ?? true}
                onChange={(e) => handleToggle(item.key, e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-5 bg-orange-50 rounded-xl p-3 border border-orange-100">
        <p className="text-gray-500 text-xs text-center">
          💡 Changes are saved automatically. We respect your privacy.
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings;